import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, ShoppingCart, User, LogIn, Package, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const MainNavigation = ({ onNavigate, currentPage = 'home', cartCount: propCartCount }) => {
  const { user, isAuthenticated, loading, loginWithGoogle, testLogin } = useAuth();
  const { cart } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use cart context length, fallback to prop for backwards compatibility
  const cartCount = cart?.length || propCartCount || 0;
  
  // Check if we're on the services tool page
  const isOnServicesPage = location.pathname === '/get-started';

  const navigateToPage = (page) => {
    setMobileMenuOpen(false); // Close mobile menu on navigation
    
    // If we're on a standalone page like /get-started, use React Router to navigate
    if (isOnServicesPage) {
      if (page === 'home') {
        navigate('/');
      } else {
        navigate(`/${page}`);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Scroll to top when navigating to any page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const scrollToContact = () => {
    setMobileMenuOpen(false); // Close mobile menu
    
    // If on services page, navigate to home and scroll to contact
    if (isOnServicesPage) {
      navigate('/#contact');
      return;
    }
    
    if (currentPage === 'home') {
      // If on home page, scroll to contact section
      const contactSection = document.querySelector('#contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on other page, go to home then scroll to contact
      navigateToPage('home');
      setTimeout(() => {
        const contactSection = document.querySelector('#contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto pl-3 pr-1.5 sm:px-6 lg:px-8 py-2.5 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Responsive sizing */}
          <button 
            onClick={() => navigateToPage('home')}
            className="flex items-center flex-shrink-0"
          >
            <img 
              src="/logo-nav.png" 
              alt="Create 3D Works Logo" 
              className="h-7 sm:h-12 w-auto object-contain"
            />
          </button>

          {/* Spacer matching menu item spacing */}
          <div className="w-4 sm:w-8 flex-shrink-0 hidden lg:block"></div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8 items-center flex-1">
            <button 
              onClick={() => navigateToPage("services")} 
              className={`font-medium transition-colors ${
                currentPage === 'services' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Services
            </button>
            <button 
              onClick={() => navigateToPage("products")} 
              className={`font-medium transition-colors ${
                currentPage === 'products' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Shop
            </button>
            <button 
              onClick={() => navigateToPage("marketplace")} 
              className={`font-medium transition-colors ${
                currentPage === 'marketplace' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Designs
            </button>
            <button 
              onClick={() => navigateToPage("about")} 
              className={`font-medium transition-colors ${
                currentPage === 'about' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              About
            </button>
            <button 
              onClick={() => navigateToPage("contact")}
              className={`font-medium transition-colors ${
                currentPage === 'contact' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Contact
            </button>
            <button 
              onClick={() => navigateToPage("home")} 
              className={`font-medium transition-colors ${
                currentPage === 'home' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Home
            </button>
          </nav>

          {/* Right-aligned Cart and Auth buttons for Desktop */}
          <div className="hidden lg:flex items-center space-x-4 ml-auto">
            {/* Launch Button - Primary CTA */}
            <a
              href="/get-started"
              className="bg-[#0007b5] hover:bg-[#000690] text-white px-5 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 shadow-md shadow-blue-500/20"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_801805a1-56d6-4a1b-9dc4-a3c271402139/artifacts/2uma28ub_Asset%205%401x.png" 
                alt="C3D Logo" 
                className="w-5 h-5 object-contain"
              />
              <span>Launch</span>
            </a>

            {/* Shopping Cart */}
            <button 
              onClick={() => navigateToPage("cart")}
              className={`relative flex items-center space-x-2 font-medium transition-colors ${
                currentPage === 'cart' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#0007b5] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
              <span className="hidden sm:inline">Cart</span>
            </button>
            
            {/* Firebase Authentication */}
            {loading ? (
              <div className="flex items-center space-x-2 text-slate-600 font-medium">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0007b5]"></div>
                <span>Loading...</span>
              </div>
            ) : isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigateToPage("profile")}
                  className={`flex items-center space-x-2 font-medium transition-colors ${
                    currentPage === 'profile' 
                      ? 'text-[#0007b5]' 
                      : 'text-slate-600 hover:text-[#0007b5]'
                  }`}
                >
                  <User className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline truncate max-w-[120px]">{user?.name || 'Profile'}</span>
                </button>
              </div>
            ) : (
              <button
                onClick={loginWithGoogle}
                className="border-2 border-[#0007b5] text-[#0007b5] hover:bg-[#0007b5] hover:text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <LogIn className="w-4 h-4" />
                <span>Sign In</span>
              </button>
            )}
          </div>

          {/* Mobile menu button - Right side actions */}
          <div className="flex lg:hidden items-center gap-1 sm:gap-3 ml-auto">
            {/* Launch button for mobile — icon scales with the button */}
            <a 
              href="/get-started"
              className="bg-[#0007b5] hover:bg-[#000690] text-white px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 text-xs sm:text-sm shadow-md shadow-blue-500/20 flex-shrink-0"
              aria-label="Launch quote tool"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_801805a1-56d6-4a1b-9dc4-a3c271402139/artifacts/2uma28ub_Asset%205%401x.png" 
                alt="" 
                className="w-3 h-3 sm:w-4 sm:h-4 object-contain"
              />
              <span>Launch</span>
            </a>

            {/* Cart icon for mobile */}
            <button 
              onClick={() => navigateToPage('cart')}
              className="relative p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Shopping cart"
              data-testid="mobile-cart-btn"
            >
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#0007b5] text-white text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center font-medium">
                  {cartCount}
                </span>
              )}
            </button>
            
            {/* Hamburger button - Simple three lines */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
              ) : (
                <div className="space-y-1.5">
                  <div className="w-5 h-0.5 sm:w-6 bg-slate-600"></div>
                  <div className="w-5 h-0.5 sm:w-6 bg-slate-600"></div>
                  <div className="w-5 h-0.5 sm:w-6 bg-slate-600"></div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-100 pt-4">
            <nav className="flex flex-col space-y-4">
              <button 
                onClick={() => navigateToPage("services")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'services' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Services
              </button>
              <button 
                onClick={() => navigateToPage("products")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'products' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Shop
              </button>
              <button 
                onClick={() => navigateToPage("marketplace")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'marketplace' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Designs
              </button>
              <button 
                onClick={() => navigateToPage("about")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'about' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                About
              </button>
              <button 
                onClick={() => navigateToPage("contact")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'contact' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Contact
              </button>
              <button 
                onClick={() => navigateToPage("home")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'home' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Home
              </button>

              {/* Divider */}
              <div className="border-t border-slate-200 my-2"></div>

              {/* Auth buttons in mobile menu */}
              {isAuthenticated && user ? (
                <>
                  {user.is_designer && (
                    <button 
                      onClick={() => navigateToPage('designer-dashboard')}
                      className="text-left flex items-center space-x-2 py-2 text-slate-600 hover:text-[#0007b5] transition-colors"
                    >
                      <Package className="w-4 h-4" />
                      <span>My Designs</span>
                    </button>
                  )}
                  <button 
                    onClick={() => navigateToPage('profile')}
                    className="text-left flex items-center space-x-2 py-2 text-slate-600 hover:text-[#0007b5] transition-colors w-full"
                  >
                    <User className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{user.name || 'Profile'}</span>
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => loginWithGoogle()}
                  className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 w-full"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default MainNavigation;